
import React, { useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Api from "services/api"
import toast from "react-hot-toast";
import ConfirmationModal from "components/common/ConfirmationMessage";
import { ListLayout } from "components/common/ListLayout";
import { CustomSwitch } from "components/common/Switch";
import AddDepartment from "components/tools/department/AddDepartment";
import { convertTimeToUTC } from "helpers/convertTimeToUTC";
import { usePermissions } from "layouts/Admin";
import { changeStatus, fetchDepartments, departmentsSelector, removeMultipleDepartments } from "slices/departmentsSlice";
import TableHeaderItem from "components/common/ListLayout/TableHeaderItem";

function DepartmentList() {
    const dispatch = useDispatch();
    const { checkPermission } = usePermissions();
    const { departments, last_page: totalPages, loader, total, current_page, per_page: pageLength } = useSelector(departmentsSelector);

    const [addForm, setAddForm] = React.useState({
        show: false,
        item: {}
    });
    const refAddutton = React.useRef(null);
    const [selectedItems, setSelectedItems] = React.useState([]);
    const [selectAll, setSelectAll] = React.useState(false);
    const [deleteModal, setDeleteModal] = React.useState(false);
    const [searchTerm, setSearchTerm] = React.useState("");
    const [sortConfig, setSortConfig] = React.useState({
        sortBy: 'department_name',
        sortDirection: 'asc',
    });

    React.useEffect(() => {
        dispatch(fetchDepartments(1))
    }, [])

    const handleAddDepartmentPopUp = (item) => {
        setAddForm({
            show: true,
            item,
        });
    };

    const hideConfirmDialog = () => {
        setDeleteModal(false);
    }

    const submitCallback = (status, message) => {
        if (status) {
            toast.success(message, {
                className: "successToast",
            })
            setSelectedItems([]);
            setSelectAll(false)
            dispatch(fetchDepartments(current_page, { search: searchTerm, sortBy: sortConfig.sortBy, sortDirection: sortConfig.sortDirection }))
            hideConfirmDialog()
        }
        else {
            toast.error(message, {
                className: "errorToast",
            });
        }
    };

    const handleRemoveItems = () => {
        dispatch(removeMultipleDepartments({ ids: selectedItems }, submitCallback))
    }

    const handleChange = (e) => {
        const id = e.target.dataset.value
        setSelectedItems(
            selectedItems.includes(id) ?
                selectedItems.filter((item) => item != id) :
                [...selectedItems, id]
        )
    };

    const handleToggleSelectAll = () => {
        if (selectAll) {
            setSelectedItems([])
            setSelectAll(false)
            return
        }
        setSelectAll(true)
        setSelectedItems(departments.map((dept) => dept.id.toString()))
    }

    const renderTableHeader = React.useCallback(() => {
        const callBack = (dir, sortBy) => {
            setSortConfig({ sortBy, sortDirection: dir })
            dispatch(fetchDepartments(current_page, { sortBy: sortBy, sortDirection: dir, search: searchTerm }))
        }

        return (
            <tr>
                <th className="col-1">
                    <div className="row_check">
                        <span>#</span>
                        {checkPermission('Delete Departments') &&
                            <label className="custom-checkbox checkbox"><input type="checkbox" name="select-all" checked={selectAll} onChange={handleToggleSelectAll} /><span className="checkmark"></span></label>
                        }
                    </div>
                </th>
                {/* <th className="col-2">
                    <TableHeaderItem
                        title="Facility"
                        sortBy="facility_id"
                        sortDirection={sortConfig.sortDirection}
                        callBack={callBack}
                    />
                </th> */}
                <th className="col-2">
                    <TableHeaderItem
                        title="Name"
                        sortBy="name"
                        sortDirection={sortConfig.sortDirection}
                        callBack={callBack}
                    />
                </th>
                <th className="col-3">
                    <TableHeaderItem
                        title="Address"
                        sortBy="address"
                        sortDirection={sortConfig.sortDirection}
                        callBack={callBack}
                    />
                </th>
                <th className="col-2">
                    <TableHeaderItem
                        title="Creation Date"
                        sortBy="created_at"
                        sortDirection={sortConfig.sortDirection}
                        callBack={callBack}
                    />
                </th>
                {checkPermission('Status Departments') &&
                    <th className="col-1">Status</th>
                }
                {checkPermission('Edit Departments') &&
                    <th className="col-1">Actions</th>
                }
            </tr>
        )
    }, [selectAll, searchTerm, sortConfig, current_page, selectedItems, departments])

    const renderTableContent = React.useCallback(() => {
        return (
            departments?.map((department, index) => (
                <DepartmentItem
                    key={department.id}
                    department={department}
                    index={index}
                    selectedItems={selectedItems}
                    handleChange={handleChange}
                    handleAddPopUp={handleAddDepartmentPopUp}
                    current_page={current_page}
                    pageLength={pageLength}
                    checkPermission={checkPermission}
                />
            ))
        )
    }, [departments, selectedItems]);

    const handleClose = () => {
        refAddutton.current.click();
        setAddForm({ show: false })
    };

    const showConfirmDialog = () => {
        setDeleteModal(true);
    };

    return (
        <ListLayout
            heading="Department"
            time={true}
            title=""
            buttonTitle="Add Department"
            renderTableHeader={renderTableHeader}
            renderTableContent={renderTableContent}
            totalPages={totalPages}
            addButtonDisabled={!checkPermission('Create Departments')}
            fetchList={fetchDepartments}
            selectedItems={selectedItems}
            handleRemoveItems={handleRemoveItems}
            renderAddForm={(callback) => <AddDepartment item={addForm.item} show={addForm.show} onClose={handleClose} callback={callback} statusPerm={checkPermission('Status Departments')} />}
            refAddutton={refAddutton}
            addForm={addForm}
            showConfirmDialog={showConfirmDialog}
            handleAddPopUp={handleAddDepartmentPopUp}
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            loader={loader}
            noOfItems={total}
            searchPlaceholder="Search department ..."
        >
            {deleteModal && <ConfirmationModal onClose={hideConfirmDialog} isOpen={deleteModal} onConfirm={handleRemoveItems} />}
        </ListLayout>
    )
}

const DepartmentItem = ({
    department, index, selectedItems, handleChange, handleAddPopUp, current_page, pageLength, checkPermission
}) => {
    const dispatch = useDispatch();
    const [status, setStatus] = useState(department?.status);

    React.useEffect(() => {
        setStatus(department?.status || 0)
    }, [department])

    const handleStatusChange = (id, s) => {
        setStatus(s ? 1 : 0)
        dispatch(changeStatus(id, { status: s ? 1 : 0 }));
    }

    const getBackgroundStyle = (status) => {
        return status === 0 ? { background: '#f4f4f4' } : {};
    };

    const serial = useMemo(() => {
        return index + 1 + (current_page - 1) * pageLength;
    }, [current_page, pageLength, index]);

    return (
        <tr>
            <td style={getBackgroundStyle(status)}>
                <div className="row_check">
                    <span>{serial}</span>
                    {checkPermission('Delete Departments') &&
                        <label className="custom-checkbox checkbox">
                            <input type="checkbox" name="select-all" data-value={department.id} checked={selectedItems.includes(department.id.toString())} onChange={handleChange} />
                            <span className="checkmark"></span>
                        </label>
                    }
                </div>
            </td>
            {/* <td style={getBackgroundStyle(status)}>
                {department.facility?.name || department.facility?.abbreviation || 'N/A'}
            </td> */}
            <td style={getBackgroundStyle(status)}>
                {department.department_name || department.name}
            </td>
            <td style={getBackgroundStyle(status)}>
                {department.address || 'N/A'}
            </td>
            <td style={getBackgroundStyle(status)}>
                {convertTimeToUTC(department.created_at)}
            </td>
            {checkPermission('Status Departments') &&
                <td style={getBackgroundStyle(status)}>
                    <CustomSwitch handleChange={(status) => handleStatusChange(department?.id, status)} checked={status == 1 ? true : false} />
                </td>
            }
            {checkPermission('Edit Departments') &&
                <td style={getBackgroundStyle(status)}>
                    <div className="row_check">
                        <button className="table_edit" data-bs-toggle="modal" data-bs-target="#exampleModal" onClick={() => handleAddPopUp({ ...department, status })}>Edit</button>
                    </div>
                </td>
            }
        </tr>
    )
}

export default DepartmentList;
